const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

/** The rung of the quiz block ladder above which the wait is quoted in days. */
export const DAY_QUOTED_LOCKOUT_MS = 4 * DAY_MS;

export interface CoolDownCopy {
  headline: string;
  subline: string;
  dateLabel: string;
}

const clockTime = (date: Date): string =>
  date.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" });

const todayOrTomorrow = (date: Date, now: number): string =>
  date.toDateString() === new Date(now).toDateString() ? "today" : "tomorrow";

const ordinal = (day: number): string => {
  if (day > 3 && day < 21) return `${day}th`;
  const suffixes: Record<number, string> = { 1: "st", 2: "nd", 3: "rd" };
  return `${day}${suffixes[day % 10] ?? "th"}`;
};

/** Hours and minutes on the ~1 day lockout rung, whole days on the ~7 day one. */
export const formatLockoutBadge = (
  blockedUntil: string,
  now: number = Date.now()
): string | null => {
  const remainingMs = new Date(blockedUntil).getTime() - now;
  if (Number.isNaN(remainingMs) || remainingMs <= 0) return null;

  if (remainingMs > DAY_QUOTED_LOCKOUT_MS) {
    return `${Math.ceil(remainingMs / DAY_MS)} days`;
  }

  const hours = Math.floor(remainingMs / HOUR_MS);
  const minutes = Math.floor((remainingMs % HOUR_MS) / MINUTE_MS);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/** Hours round up so the last stretch never reads "in 0 hours". */
export const formatCoolDownCopy = (
  coolDownEndsAt: string,
  now: number = Date.now()
): CoolDownCopy | null => {
  const endsAt = new Date(coolDownEndsAt);
  const remainingMs = endsAt.getTime() - now;
  if (Number.isNaN(remainingMs) || remainingMs <= 0) return null;

  const hours = Math.ceil(remainingMs / HOUR_MS);
  const headline =
    hours === 1
      ? "You can invest in 1 hour"
      : `You can invest in ${hours} hours`;
  const subline = `Opens ${clockTime(endsAt)} ${todayOrTomorrow(endsAt, now)}.`;
  const dateLabel = endsAt.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return { headline, subline, dateLabel };
};

/** Says when the quiz reopens: a clock time on the short rung, a date beyond it. */
export const formatLockoutCopy = (
  blockedUntil: string,
  now: number = Date.now()
): string | null => {
  const endsAt = new Date(blockedUntil);
  const remainingMs = endsAt.getTime() - now;
  if (Number.isNaN(remainingMs) || remainingMs <= 0) return null;

  if (remainingMs > DAY_QUOTED_LOCKOUT_MS) {
    const month = endsAt.toLocaleDateString("en-GB", { month: "long" });
    return `You can try again on ${ordinal(
      endsAt.getDate()
    )} ${month} ${endsAt.getFullYear()}.`;
  }

  return `You can try again after ${clockTime(endsAt)} ${todayOrTomorrow(
    endsAt,
    now
  )}.`;
};
